// @flow

import { useState } from 'react';

import { Trash } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { selectCartIsWorking, selectProductsByType } from '@gandi/cart';

import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';

import { IconButton } from '@gandi/ui';

import { useAppSelector } from 'src/hooks';

import type { ProductSummary } from 'src/modules/mailbox-standalone/resources/products';

import { useRemoveCartMailboxes } from '../../../modules/hooks/useRemoveCartMailboxes';

import { ActionsButton } from './ActionsButton';

import styles from './DeleteChoice.scss';

export function DeleteChoice({
  product,
  productType,
}: {
  product: ProductSummary,
  productType: { productType: string },
}): React.MixedElement {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const cartProducts = useAppSelector(state =>
    selectProductsByType(state, productType),
  );

  const isCartWorking = useAppSelector(selectCartIsWorking);

  const cartProductItem = cartProducts?.find(
    item => item.payload.config_name === product.config_name,
  );
  const [open, setOpen] = useState(false);

  const { handleRemoveCartMailboxes } = useRemoveCartMailboxes();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = () => {
    (async () => {
      try {
        await handleRemoveCartMailboxes(cartProductItem?.ids || []);
        setOpen(false);
      } catch (err) {
        dispatch(
          notifyDanger({
            message:
              err.response?.data.message ||
              __('We are unable to remove mailboxes from your cart.'),
          }),
        );
      }
    })();
  };

  return (
    <>
      {cartProductItem && !open && (
        <div className={styles.deleteChoiceContainer}>
          <IconButton
            className={styles.deleteButton}
            type="button"
            small
            kind="ghost"
            onClick={handleOpen}
          >
            <Trash className={styles.trashIcon} />
            {__('Delete')}
          </IconButton>
        </div>
      )}
      {open && (
        <div className={styles.confirmContainer}>
          <div className={styles.overlay}>
            <p className={styles.question}>{__('Confirm removal?')}</p>
            <div className={`${styles.buttons} ${styles.mobile}`}>
              <ActionsButton
                isMobile
                handleClose={handleClose}
                handleDelete={handleDelete}
                isLoading={isCartWorking}
              />
            </div>
            <div className={`${styles.buttons} ${styles.desktop}`}>
              <ActionsButton
                isMobile={false}
                handleClose={handleClose}
                handleDelete={handleDelete}
                isLoading={isCartWorking}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
